import { captureException } from '@sentry/react';
import { ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import { getFormatterModelForField3, IField3, ISelectMeta } from '../data-models/field3.data-model';
import { IDisplayField } from '../view-models/display-field.view-model';
import { FormatterFn, IPartialFormatterDataModel, StandardFormatterId } from '@foresightdata/web-common-core';
import { defaultFormatter, FormatterService } from '@foresightdata/web-common-formatter-service';
import { isObject } from './object-util';

export class AgFormatterService {
  static #instance: AgFormatterService | undefined;
  /** `<formatter>|<colId>` pairs already reported, so a broken formatter reports once, not once per cell. */
  readonly #reportedFailures = new Set<string>();

  getFormatterForFieldV3<TData, TValue>(field: IField3<unknown>): ValueFormatterFunc<TData, TValue> {
    const model = getFormatterModelForField3(field);

    return this.#createFormatter(model);
  }

  getFormatterForId<TData, TValue>(formatterId: StandardFormatterId): ValueFormatterFunc<TData, TValue> {
    return this.#createFormatterForId(formatterId);
  }

  getFormatterForFormField<TData, TValue>(field: IDisplayField<unknown>) {
    const formatter = FormatterService.get().getFormatterForFormField(field);

    return this.#createGridFormatter<TData, TValue>(formatter, formFieldFormatterId(field));
  }

  getFormatterForModel<T>(formatterDataModel: IPartialFormatterDataModel<T>) {
    if (formatterDataModel.id) {
      this.#createFormatter(formatterDataModel);
    }

    return this.#createFormatter(formatterDataModel);
  }

  #createFormatter<TData, TValue>(
    formatterDataModel: IPartialFormatterDataModel<unknown>
  ): ValueFormatterFunc<TData, TValue> {
    const formatter = FormatterService.get().getFormatterForModel(formatterDataModel);

    return this.#createGridFormatter<TData, TValue>(
      formatter,
      formatterDataModel.id ?? String(formatterDataModel.type)
    );
  }

  #createFormatterForId<TData, TValue>(formatterId: StandardFormatterId): ValueFormatterFunc<TData, TValue> {
    const formatter = FormatterService.get().getFormatterForId(formatterId);

    if (formatter === defaultFormatter) {
      return defaultAgFormatter as ValueFormatterFunc<TData, TValue>;
    }

    return this.#createGridFormatter<TData, TValue>(formatter, formatterId);
  }

  #createGridFormatter<TData, TValue>(
    formatter: FormatterFn<TValue>,
    formatterId: string,
    fieldPath?: string
  ): ValueFormatterFunc<TData, TValue> {
    return (params) => {
      // A tree node row is `node.group` too, but carries its own data — blanking is the
      // row-group case only.
      const treeRowWithData = params.api?.getGridOption?.('treeData') === true && params.node?.data != null;
      if (
        !treeRowWithData &&
        params.column.getAggFunc() === undefined &&
        params.node?.group &&
        !params.column.isRowGroupActive()
      ) {
        return '';
      }

      const value = getCellValue(params, fieldPath);

      try {
        return formatter(value);
      } catch (error) {
        // A formatter that throws takes the whole grid down with it (the set filter, sorting,
        // export), so the cell shows the raw value instead and the failure reports once.
        this.#reportFormatterFailure(error, formatterId, params.column?.getColId?.(), value);
        return rawValueText(value);
      }
    };
  }

  #reportFormatterFailure(error: unknown, formatterId: string, colId: string | undefined, value: unknown) {
    const reportKey = `${formatterId}|${colId}`;
    if (this.#reportedFailures.has(reportKey)) {
      return;
    }
    this.#reportedFailures.add(reportKey);
    captureException(error, {
      tags: { type: 'grid_formatter_error' },
      extra: { colId, formatterId, valueType: typeof value, valueIsArray: Array.isArray(value) },
    });
  }

  static initService() {
    if (!this.#instance) {
      this.#instance = new AgFormatterService();
    }

    return this.#instance;
  }

  static get() {
    if (!this.#instance) {
      this.#instance = this.initService();
    }
    return this.#instance;
  }

  static destroyService() {
    this.#instance = undefined;
  }
}

/** The formatter web-common's `getFormatterForFormField` picks: the field's own unless it formats values to labels. */
function formFieldFormatterId(field: IDisplayField<unknown>): string {
  const usesFieldFormatter =
    field.formatter && !(field.rendererMeta as ISelectMeta<unknown> | undefined)?.formatValueToLabel;
  if (usesFieldFormatter) {
    const id = typeof field.formatter === 'string' ? field.formatter : field.formatter?.id;
    if (id) {
      return id;
    }
  }
  return `${field.renderer}_${field.key}`;
}

/** `String(value ?? '')`, except that it cannot throw: an object with no `toString` gives ''. */
export function rawValueText(value: unknown): string {
  try {
    return String(value ?? '');
  } catch {
    return '';
  }
}

export function defaultAgFormatter<TData, TValue>(params: ValueFormatterParams<TData, TValue>) {
  return params.value;
}

function getCellValue<TData>(params: ValueFormatterParams<TData>, fieldPath?: string) {
  if (fieldPath) {
    return get(params.data, fieldPath);
  }
  if (params.node?.group && isObject(params.value)) {
    return 'value' in params.value ? params.value.value : params.value;
  }
  return params.value;
}
